import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useCallback } from "react";
import { useTranslations } from "next-intl";
import isEqual from "lodash/isEqual";
import { format } from "date-fns/format";
import { de as LocaleDe } from "date-fns/locale/de";
import { analytics } from "@silvertours/front-entities";
import { String, Session } from "@silvertours/front-shared";
import { I18nLegacy } from "@silvertours/front-legacy-shared";
import { formatDate as formatDateForForm, DEFAULT_DEPARTURE_DATE, isDeptDateInLessThanDays, isDestDateBeforeDepDate, useSearchContext, DEFAULT_DESTINATION_DATE } from "../context";
import { Wrapper, Headline, List, ItemButton, Destination, Duration, DeleteButton } from "./SearchHistory.styles";
var SEARCH_HISTORY_SESSION_STORAGE_KEY = "SearchHistoryKey8934087340";
var SEARCH_HISTORY_LIMIT = 6;
export var getSearchHistory = function() {
    var data = Session.SessionStorageManager.getData(SEARCH_HISTORY_SESSION_STORAGE_KEY);
    return {
        items: data || []
    };
};
export var mapFormToSearchItem = function(formBody) {
    var ref, ref1, ref2, ref3, ref4, ref5, ref6, ref7;
    return {
        depAirportCode: ((ref = formBody.depLocation) === null || ref === void 0 ? void 0 : ref.iataCode) || ((ref1 = formBody.depLocation) === null || ref1 === void 0 ? void 0 : ref1.cityCode) || "",
        destAirportCode: ((ref2 = formBody.destLocation) === null || ref2 === void 0 ? void 0 : ref2.iataCode) || ((ref3 = formBody.destLocation) === null || ref3 === void 0 ? void 0 : ref3.cityCode) || "",
        destName: ((ref4 = formBody.destLocation) === null || ref4 === void 0 ? void 0 : ref4.cityName) || ((ref5 = formBody.destLocation) === null || ref5 === void 0 ? void 0 : ref5.name) || "",
        depName: ((ref6 = formBody.depLocation) === null || ref6 === void 0 ? void 0 : ref6.cityName) || ((ref7 = formBody.depLocation) === null || ref7 === void 0 ? void 0 : ref7.name) || "",
        depDate: formBody.depDate.split("T")[0],
        depTime: formBody.depDate.split("T")[1],
        destDate: formBody.destDate.split("T")[0],
        destTime: formBody.destDate.split("T")[1],
        depWeekDay: "",
        destWeekDay: "",
        imageUrl: ""
    };
};
var mapHistoryItemToForm = function(item) {
    var depName = item.depName, depDate = item.depDate, depTime = item.depTime, destName = item.destName, destDate = item.destDate, destTime = item.destTime, depAirportCode = item.depAirportCode, destAirportCode = item.destAirportCode;
    var depDateTime = "".concat(depDate, "T").concat(depTime);
    var destDateTime = "".concat(destDate, "T").concat(destTime);
    var revertToDefaultDates = isDeptDateInLessThanDays(depDateTime, 0) || isDestDateBeforeDepDate(destDateTime, depDateTime);
    return {
        depLocation: {
            name: depName,
            cityCode: depAirportCode,
            type: depAirportCode ? "airport" : "city"
        },
        depDate: revertToDefaultDates ? formatDateForForm(DEFAULT_DEPARTURE_DATE) : depDateTime,
        destLocation: destName ? {
            cityCode: destAirportCode,
            name: destName,
            type: destAirportCode ? "airport" : "city"
        } : null,
        destDate: revertToDefaultDates ? formatDateForForm(DEFAULT_DESTINATION_DATE) : destDateTime
    };
};
export var addItemToSearchHistory = function(item) {
    var history = getSearchHistory();
    if (!history.items.find(function(storedItem) {
        return isEqual(storedItem, item);
    })) {
        history.items.unshift(item);
        if (history.items.length > SEARCH_HISTORY_LIMIT) {
            history.items.pop();
        }
        Session.SessionStorageManager.setData(SEARCH_HISTORY_SESSION_STORAGE_KEY, history.items);
    }
};
export var clearSearchHistory = function() {
    Session.SessionStorageManager.removeData(SEARCH_HISTORY_SESSION_STORAGE_KEY);
};
export var SearchHistory = function(param) {
    var onItemClick = param.onItemClick, _items = param.items, items = _items === void 0 ? [] : _items, onLastElementBlur = param.onLastElementBlur;
    var t = useTranslations("features.stageLegacy.stage.searchForm");
    var language = I18nLegacy.useLocale().language;
    var ref = useSearchContext(), setSearchFromHistory = ref.setSearchFromHistory, clearHistoryState = ref.clearSearchHistory;
    var handleSetItem = useCallback(function(item) {
        setSearchFromHistory(mapHistoryItemToForm(item));
        analytics.gtm.trackSearchHistorySelect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var handleClearItem = useCallback(function() {
        clearSearchHistory();
        clearHistoryState();
        analytics.gtm.trackSearchHistoryDelete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var localDateFormats = String.dateFormats[language];
    var formatDateLong = function(value) {
        return format(new Date(value), localDateFormats.dateLong, {
            locale: LocaleDe
        });
    };
    var formatDuration = function(start, end) {
        return [
            formatDateLong(start),
            formatDateLong(end)
        ].join(" – ");
    };
    var filteredItems = items.filter(function(item) {
        return item.language === language || item.language === undefined;
    });
    if (filteredItems.length === 0) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(Wrapper, {
        children: [
            /*#__PURE__*/ _jsx(Headline, {
                children: t("searchHistory.title")
            }),
            /*#__PURE__*/ _jsx(List, {
                children: filteredItems.map(function(item, index) {
                    return /*#__PURE__*/ _jsx("li", {
                        children: /*#__PURE__*/ _jsxs(ItemButton, {
                            onMouseDown: function(e) {
                                e.stopPropagation();
                                handleSetItem(item);
                                onItemClick();
                            },
                            children: [
                                /*#__PURE__*/ _jsxs(Destination, {
                                    children: [
                                        item.depName,
                                        " ",
                                        item.destName ? " - ".concat(item.destName) : ""
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Duration, {
                                    children: formatDuration(item.depDate, item.destDate)
                                })
                            ]
                        })
                    }, item.depName + index);
                })
            }),
            /*#__PURE__*/ _jsx(DeleteButton, {
                onMouseDown: handleClearItem,
                onBlur: onLastElementBlur,
                children: t("searchHistory.deleteLink")
            })
        ]
    });
};
